import TextField from '@material-ui/core/TextField';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import DeleteBin from 'utils/images/bin.svg';
import updateCheckbox from '../../../utils/update-checkbox';
import Allergens from '../Allergens';
import CostPriceField from '../CostPriceField';
import VatRateField from '../VatRateField';
import LimitSelect from '../LimitSelect';
import PriceField from '../PriceField';
import StatusSelect from '../StatusSelect';

const Container = styled.div``;

const Field = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: nowrap;
  clear: both;
  padding-top: 6px;
  gap: 0;
  overflow-x: auto;
`;

const OptionContainer = styled.div`
  margin: 0 20px 0 0;
  display: block;
  width: 300px;

  @media (max-width: 700px) {
    width: calc((175 / 350) * 100vw - 5px);
    margin-right: calc((40 / 350) * 100vw - 31px);
  }

  & > label {
    display: inline-block;
    color: #797979;
    padding-bottom: 20px;
    letter-spacing: -0.2px;
  }
`;

const StyledPriceField = styled(PriceField)`
  margin: 0 12px 0 0;
  display: block;

  @media (max-width: 700px) {
    margin-right: calc((15 / 350) * 100vw - 10px);
  }

  & > label {
    display: inline-block;
    color: #797979;
    padding-bottom: 20px;
  }
`;

const StyledCostPriceField = styled(CostPriceField)`
  margin: 0 12px 0 0;
  display: block;

  @media (max-width: 700px) {
    margin-right: calc((15 / 350) * 100vw - 10px);
  }

  & > label {
    display: inline-block;
    color: #797979;
    padding-bottom: 20px;
  }
`;

const StyledVatRateField = styled(VatRateField)`
  margin: 0 12px 0 0;
  display: block;

  @media (max-width: 700px) {
    margin-right: calc((15 / 350) * 100vw - 10px);
  }

  & > label {
    display: inline-block;
    color: #797979;
    padding-bottom: 20px;
    white-space: nowrap;
  }
`;

const DeleteContainer = styled.div`
  padding: 10px 18px 4px;

  ${props =>
    props.hideLabel &&
    css`
      padding: 0px 0px 2px 22px;
    `}
`;

const DeleteIcon = styled.img`
  width: 14px;
  cursor: pointer;
`;

const AllergensButton = styled.span`
  font-family: 'Basier Circle';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.2px;
  color: #000000;
  background: #ffffff;
  border: 1px solid;
  border-color: ${props => (props.selected ? 'black' : '#000')};
  box-sizing: border-box;
  border-radius: 49px;
  padding: 6px 12px;
  margin-right: 10px;
  margin-top: ${props => (props.hideLabel ? '0px' : '22px')};
  width: ${props => (props.isSingleChoice ? '126px' : '116px')};
  height: 30px;
  flex-shrink: 0;

  :hover {
    cursor: pointer;
    border-color: black;
  }
`;

const AllergensContainer = styled.div`
  margin-top: 10px;
`;

function Option(props) {
  return (
    <OptionContainer>
      <label
        htmlFor={props.id}
        style={{ display: props.hideLabel ? 'none' : '' }}
      >
        Choice
      </label>
      <TextField
        id={props.id}
        size="small"
        value={props.value}
        placeholder={props.placeholder || 'e.g. Lemonade, Pepperoni or Fries'}
        disabled={props.disabled}
        className="editable-title"
        onChange={props.onChange}
        fullWidth={true}
        style={{ width: '300px' }}
      />
    </OptionContainer>
  );
}

function OptionsField(props) {
  const { id, option, onChange, onDelete, showLimit } = props;

  const handleOptionChange = key => e => {
    onChange({ ...option, [key]: e.target.value });
  };

  const handleDelete = () => onDelete(option);

  const handleCheckboxesChange = key => (selected, isChecked) => {
    const toBeUpdated = option[key];
    const updated = updateCheckbox(toBeUpdated, selected, isChecked);
    onChange({ ...option, [key]: updated });
  };

  const [showAllergens, setShowAllergens] = useState(false);
  const handleShowAllergens = () => setShowAllergens(!showAllergens);

  return (
    <Container>
      <Field>
        <Option
          id={`name-${id}`}
          onChange={handleOptionChange('name')}
          placeholder={props.optionPlaceholder}
          hideLabel={props.hideLabel}
          value={option.name}
        />
        <StyledPriceField
          id={`price-${id}`}
          width={100}
          onChange={handleOptionChange('price')}
          placeholder={props.pricePlaceholder}
          hideLabel={props.hideLabel}
          labelAdornment="+ "
          value={option.price}
          style={{ width: '70px' }}
        />
        <StyledCostPriceField
          id={`cost-price-${id}`}
          onChange={handleOptionChange('cost_price')}
          hideLabel={props.hideLabel}
          value={option.cost_price}
          price={option.price}
        />
        <StyledVatRateField
          id={`vat-rate-eat-in-${id}`}
          label="Eat-in VAT %"
          onChange={handleOptionChange('vat_rate_eat_in')}
          hideLabel={props.hideLabel}
          value={option.vat_rate_eat_in}
        />
        <StyledVatRateField
          id={`vat-rate-takeaway-${id}`}
          label="Takeaway VAT %"
          onChange={handleOptionChange('vat_rate_takeaway')}
          hideLabel={props.hideLabel}
          value={option.vat_rate_takeaway}
        />
        <AllergensButton
          selected={showAllergens}
          onClick={handleShowAllergens}
          isSingleChoice={showLimit}
          hideLabel={props.hideLabel}
        >
          Diet & Allergens
        </AllergensButton>
        {props.showLimit ? (
          <LimitSelect
            id={`limit-${id}`}
            onChange={handleOptionChange('choice_limit')}
            hideLabel={props.hideLabel}
            variant={'OptionsField'}
            value={option.choice_limit}
          />
        ) : null}
        <StatusSelect
          id={`status-${id}`}
          onChange={handleOptionChange('status')}
          hideLabel={props.hideLabel}
          variant={'OptionsField'}
          value={option.status}
        />
        <DeleteContainer hideLabel={props.hideLabel}>
          {props.disableDelete ? null : (
            <DeleteIcon src={DeleteBin} alt="Delete" onClick={handleDelete} />
          )}
        </DeleteContainer>
      </Field>
      <AllergensContainer>
        {showAllergens ? (
          <Allergens
            id={`allergens-select-${props.id}`}
            allergens={option.allergens || []}
            embedded={true}
            dietaryLabels={option.dietaryLabels || []}
            onAllergensSet={handleOptionChange('allergens')}
            onAllergensChange={handleCheckboxesChange('allergens')}
            onDietaryLabelsChange={handleCheckboxesChange('dietaryLabels')}
          />
        ) : null}
      </AllergensContainer>
    </Container>
  );
}

OptionsField.propTypes = {
  id: PropTypes.string.isRequired,
  option: PropTypes.shape({
    name: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    cost_price: PropTypes.number,
    vat_rate_eat_in: PropTypes.number,
    vat_rate_takeaway: PropTypes.number,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  hideLabel: PropTypes.bool,
  disableDelete: PropTypes.bool,
  showLimit: PropTypes.bool,
};

export default OptionsField;



// WEBPACK FOOTER //
// ./src/components/Dashboard/Panels/MenuEditor/components/shared/OptionsField/index.jsx